<template>
  <div>
    <el-dialog
      :visible.sync="dialogVisible"
      :title="$t('merchantAccount.cash_out')"
      width="800px"
      class="diglog-card multi-mch-withdraw-modal"
      :close-on-click-modal="false"
      @closed="handleDialogClosed"
    >
      <div class="diglog-container">
        <div class="header-line" />
        <div class="section-title">{{ $t('data.amount_details') }}</div>
        <div class="section-tip">{{ $t('tips.cash_out_amount_enter') }}</div>

        <el-form ref="withdrawForm" :model="withdrawForm" :rules="rules" status-icon label-position="top">
          <el-row>
            <el-col :span="11">
              <el-form-item prop="withdraw_money">
                <template slot="label">
                  <span>
                    {{ $t('data.cash_out_amount') }}
                    <span v-if="withdrawThreshold" class="threshold-tip">
                      ({{ $t('data.cash_out_amount_should') }} ≥ {{ withdrawThreshold | toDivideWithDot }})
                    </span>
                  </span>
                </template>
                <el-input
                  v-model="withdrawForm.withdraw_money"
                  placeholder="Enter amount"
                  type="text"
                  class="amount-input"
                  autocomplete="off"
                  @input="handleAmountInput"
                >
                  <el-select
                    slot="prepend"
                    v-model="withdrawForm.currency"
                    :placeholder="$t('data.currency')"
                    @change="handleCurrencyChange"
                  >
                    <el-option
                      v-for="(item, index) in currencyOptions"
                      :key="item.currency || index"
                      :label="item.currency"
                      :value="item.currency"
                    />
                  </el-select>
                </el-input>
              </el-form-item>

              <div v-if="selectedCountryCode === 'PH'" class="section-tip country-tip">
                <template v-if="Number(withdrawForm.withdraw_money) > 0">
                  <span v-if="Number(withdrawForm.withdraw_money) <= 50000">{{ $t('tips.withdraw_tip_less', { currency: selectedCurrency }) }}</span>
                  <span v-else>{{ $t('tips.withdraw_tip_more', { currency: selectedCurrency }) }}</span>
                </template>
                <template v-else>
                  <span>{{ $t('tips.withdraw_tip_less', { currency: selectedCurrency }) }}; {{ $t('tips.withdraw_tip_more', { currency: selectedCurrency }) }}</span>
                </template>
              </div>
            </el-col>

            <el-col :span="11" :offset="2">
              <el-form-item>
                <div slot="label">
                  <span>{{ $t('tips.dashboard_balance_number') }}</span>
                  <el-tooltip placement="top">
                    <div slot="content" style="width: 300px">
                      <span>{{ $t('tips.dashboard_balance_question') }}</span>
                    </div>
                    <svg-icon icon-class="icon-font-question" class="icon-question" />
                  </el-tooltip>
                </div>
                <div class="input-show-info">
                  {{ selectedCurrency || '-' }} {{ availableWithdraw | toDivideWithDot }}
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <div class="line-bottom-1px" />
          <div class="section-title">{{ $t('data.bank_details') }}</div>
          <div class="section-tip">{{ $t('tips.new_cash_out_account') }}</div>

          <el-row>
            <el-col :span="11">
              <el-form-item :label="$t('merchant.withdrawal_account')" prop="bank_no">
                <el-select
                  v-model="withdrawForm.bank_no"
                  class="account-select"
                  size="small"
                  :placeholder="$t('data.account')"
                  :disabled="!selectedCurrency"
                  clearable
                  @change="handleAccountChange"
                >
                  <el-option
                    v-for="item in accountList"
                    :key="item.bank_no"
                    :label="getAccountLabel(item)"
                    :value="item.bank_no"
                  />
                </el-select>
                <div v-if="selectedCurrency && !accountLoading && accountList.length === 0" class="account-empty-tip">
                  <span>{{ $t('tips.not_bank_account') }}</span>
                  <span class="add-account-link" @click="$router.push({ name: 'Financial_Manage_Setting' })">Go to Add</span>
                </div>
              </el-form-item>
            </el-col>

            <el-col v-if="showExchangeRate" :span="11" :offset="withdrawFeeStatus === 1 ? 2 : 0">
              <div class="exchange-rate-wrapper">
                <span class="exchange-rate-text">{{ exchangeRateText }}</span>
                <el-button
                  size="mini"
                  class="refresh-rate-button"
                  :loading="rateLoading"
                  disabled
                >
                  {{ rateRefreshButtonText }}
                </el-button>
              </div>
            </el-col>
          </el-row>

          <el-row v-if="withdrawFeeStatus === 1 || showExchangeRate">
            <el-col v-if="withdrawFeeStatus === 1" :span="11">
              <el-form-item>
                <div slot="label">
                  <span>{{ $t('dashboard.fee') }}</span>
                  <el-tooltip placement="top" :content="$t('tips.fee_tip')">
                    <svg-icon icon-class="icon-font-question" class="icon-question" />
                  </el-tooltip>
                </div>
                <div class="readonly-amount">
                  <span>{{ withdrawFeeCurrency || '-' }}</span>
                  <span>{{ formattedWithdrawFee }}</span>
                </div>
              </el-form-item>
            </el-col>

          </el-row>

          <el-row v-if="!hideUsdManualQuoteArrival">
            <el-col :span="11">
              <el-form-item :label="$t('data.amount_received')">
                <div class="readonly-amount">
                  <span>{{ arrivalCurrency || '-' }}</span>
                  <span>{{ arrivalAmount }}</span>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row v-if="isInvoiceRequired">
            <el-col :span="24">
              <el-form-item
                :label="$t('data.invoice_image')"
                prop="invoice_files"
                :required="isInvoiceRequired"
              >
                <div v-if="withdrawForm.invoice_files.length" class="invoice-file-list">
                  <div
                    v-for="(file, index) in withdrawForm.invoice_files"
                    :key="file.invoice_file_url"
                    class="invoice-file-item"
                  >
                    <i class="el-icon-document" />
                    <span>{{ file.invoice_file_name }}</span>
                    <i class="el-icon-close invoice-file-remove" @click="removeInvoiceFile(index)" />
                  </div>
                </div>
                <MultipleUploadFile
                  :list="withdrawForm.invoice_files"
                  :limit-num="5"
                  accept-type=".jpg,.jpeg,.png,.webp,.pdf,.PDF,.JPG,.JPEG,.PNG,.WEBP"
                  file-dir="MERCHANT_WITHDRAW_INVOICE"
                  @templateFileSuccess="templateFileSuccess"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button type="info" class="btn-submit" @click="onClickNext">{{ $t('components.next') }}</el-button>
      </div>
    </el-dialog>

    <MultiMchWithdrawConfirmModal ref="confirmModal" @success="handleWithdrawSuccess" @closed="resetWithdrawState" />
    <WithdrawSuccessModal ref="withdrawSuccessModal" />
  </div>
</template>

<script>
import { fetchMchMultiInfo } from '@/api/dashboard/dashboard'
import {
  multiMchWithdrawBankQueryList
} from '@/api/merchant-manage/merchant-manage'
import {
  fetchMultiMchWithdrawType
} from '@/api/system/system'
import { fetchMultiMchAllWithdrawFee, fetchMultiMchWithdrawFee, fetchWithdrawExchangeRate } from '@/api/system/system'
import MultipleUploadFile from '@/views/components/Upload/MultipleUploadFile.vue'
import MultiMchWithdrawConfirmModal from './MultiMchWithdrawConfirmModal.vue'
import WithdrawSuccessModal from './WithdrawSuccessModal.vue'

export default {
  name: 'Multi_Mch_Withdraw_Modal',
  components: {
    MultipleUploadFile,
    MultiMchWithdrawConfirmModal,
    WithdrawSuccessModal
  },
  data() {
    this.validateAmount = (rule, value, callback) => {
      const amount = Number(value)
      this.$commonFn.validateFormAmount({ rule, value: amount, countryCode: this.selectedCountryCode, callback })
      if (!this.withdrawForm.currency) {
        return callback(new Error(this.$t('data.currency_required')))
      }
      if (amount <= 0 || isNaN(amount)) {
        return callback(new Error('Please enter a non-negative number'))
      }
      if (amount > 10000000000) {
        return callback(new Error('Currently up to 100 billion'))
      }
      if (!/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(String(value))) {
        return callback(new Error('Please enter two decimal places'))
      }
      if (amount > this.$commonFn.mathDivide(this.availableWithdraw, 100)) {
        return callback(new Error('Insufficient balance!'))
      }
      if (this.withdrawThreshold && amount < this.$commonFn.mathDivide(this.withdrawThreshold, 100)) {
        return callback(new Error(`Cashout Threshold: ${this.formatMinorAmount(this.withdrawThreshold)} ${this.selectedCurrency}`))
      }
      if (amount <= this.$commonFn.mathDivide(this.currentWithdrawFee, 100)) {
        return callback(new Error(`Amount greater than ${this.formattedWithdrawFee} ${this.withdrawFeeCurrency}`))
      }
      callback()
    }

    this.validateInvoiceFiles = (rule, value, callback) => {
      if (this.isInvoiceRequired && (!value || value.length === 0)) {
        return callback(new Error(this.$t('tips.please_upload_image_or_pdf')))
      }
      callback()
    }

    return {
      dialogVisible: false,
      openingConfirm: false,
      accountLoading: false,
      withdrawForm: this.createEmptyForm(),
      balanceList: [],
      currencyOptions: [],
      accountList: [],
      withdrawFee: 0,
      usdWithdrawFee: 0,
      withdrawFeeStatus: 0,
      withdrawThreshold: 0,
      quotationType: null,
      quoteId: null,
      exchangeRate: 1,
      rateLoading: false,
      rateRefreshSeconds: 0,
      rateRefreshTimer: null,
      rateRequestId: 0,
      rules: {
        withdraw_money: [
          { required: true, message: 'Amount cannot be empty', trigger: 'blur' },
          { validator: this.validateAmount, trigger: 'blur' }
        ],
        bank_no: [
          { required: true, message: 'Account cannot be empty', trigger: ['blur', 'change'] }
        ],
        invoice_files: [
          { validator: this.validateInvoiceFiles, trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    selectedCurrency() {
      return this.withdrawForm.currency
    },
    selectedCountryCode() {
      const selectedCountry = this.$store.state.page.countryArr.find(item => item.currency === this.selectedCurrency)
      return selectedCountry ? selectedCountry.code : ''
    },
    selectedBalance() {
      return this.balanceList.find(item => item.merchant && item.merchant.currency === this.selectedCurrency) || {}
    },
    availableWithdraw() {
      return this.selectedBalance.balance ? Number(this.selectedBalance.balance.available_withdraw) || 0 : 0
    },
    selectedAccount() {
      return this.accountList.find(item => item.bank_no === this.withdrawForm.bank_no)
    },
    isInvoiceRequired() {
      return this.quotationType === 2 && this.selectedCurrency !== 'USD' &&
        this.arrivalCurrency === 'USD'
    },
    hideUsdManualQuoteArrival() {
      return this.quotationType === 1 && this.arrivalCurrency === 'USD'
    },
    arrivalCurrency() {
      if (!this.selectedAccount) {
        return this.selectedCurrency
      }
      return this.selectedAccount.settlement_type === 2 ? 'USD' : this.selectedCurrency
    },
    currentWithdrawFee() {
      if (this.withdrawFeeStatus !== 1) {
        return 0
      }
      const isUsdWithdraw = this.selectedCurrency !== 'USD' && this.arrivalCurrency === 'USD'
      const fee = isUsdWithdraw ? this.usdWithdrawFee : this.withdrawFee
      return Number(fee) || 0
    },
    formattedWithdrawFee() {
      return this.formatMinorAmount(this.currentWithdrawFee)
    },
    withdrawFeeCurrency() {
      return this.selectedAccount && this.selectedAccount.settlement_type === 2
        ? 'USD'
        : this.selectedCurrency
    },
    showExchangeRate() {
      return Boolean(this.quotationType === 2 && this.selectedAccount && this.selectedAccount.settlement_type === 2 &&
        this.arrivalCurrency && this.selectedCurrency &&
        !(this.selectedCurrency === 'USD' && this.arrivalCurrency === 'USD'))
    },
    exchangeRateText() {
      if (!this.showExchangeRate || !this.exchangeRate) {
        return '-'
      }
      return `1.00 ${this.arrivalCurrency} = ${this.exchangeRate} ${this.selectedCurrency}`
    },
    formattedRateCountdown() {
      const minutes = Math.floor(this.rateRefreshSeconds / 60)
      const seconds = this.rateRefreshSeconds % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    rateRefreshButtonText() {
      return this.rateRefreshSeconds > 0
        ? this.$t('data.refresh_exchange_rate_after', { time: this.formattedRateCountdown })
        : this.$t('data.refresh_exchange_rate')
    },
    arrivalAmount() {
      const amount = Number(this.withdrawForm.withdraw_money)
      if (!amount || !this.selectedAccount || !this.exchangeRate) {
        return '0.00'
      }
      const amountInMinor = this.$commonFn.mathMultiply(amount, 100)
      const amountInArrivalMinor = this.arrivalCurrency === this.selectedCurrency
        ? amountInMinor
        : this.$commonFn.mathDivide(amountInMinor, this.exchangeRate)
      const netAmount = Math.max(amountInArrivalMinor - this.currentWithdrawFee, 0)
      const arrivalAmount = this.$commonFn.mathDivide(netAmount, 100)
      return this.formatMajorAmount(arrivalAmount)
    }
  },
  beforeDestroy() {
    this.stopRateRefreshTimer()
  },
  methods: {
    createEmptyForm() {
      return {
        currency: undefined,
        withdraw_money: '',
        bank_no: undefined,
        invoice_files: []
      }
    },
    changModalStatus(value) {
      this.dialogVisible = value
      if (value) {
        this.getBalanceList()
        this.getCurrencyOptions()
      }
    },
    getCurrencyOptions() {
      return fetchMultiMchAllWithdrawFee().then(res => {
        const data = Array.isArray(res.data) ? res.data : []
        this.currencyOptions = data.filter(item => item && item.currency)
      })
    },
    getBalanceList() {
      return fetchMchMultiInfo().then(res => {
        const { mch_info_resp_list = [] } = res.data || {}
        this.balanceList = mch_info_resp_list
      })
    },
    handleCurrencyChange(currency) {
      this.withdrawForm.withdraw_money = ''
      this.withdrawForm.bank_no = undefined
      this.withdrawForm.invoice_files = []
      this.accountList = []
      this.withdrawFee = 0
      this.usdWithdrawFee = 0
      this.withdrawFeeStatus = 0
      this.withdrawThreshold = 0
      this.quotationType = null
      this.quoteId = null
      this.exchangeRate = 1
      this.rateRequestId += 1
      this.rateLoading = false
      this.stopRateRefreshTimer()
      if (!currency) {
        return
      }
      this.getBankList(currency)
      this.getWithdrawFee(currency)
      this.getWithdrawThreshold(currency)
      this.$nextTick(() => this.$refs.withdrawForm.clearValidate())
    },
    getBankList(currency) {
      this.accountLoading = true
      multiMchWithdrawBankQueryList({ currency, audit_status: 2, use_status: 1 }).then(res => {
        const data = res.data || []
        this.accountList = data.filter(item => item.audit_status === 2)
      }).finally(() => {
        this.accountLoading = false
      })
    },
    getWithdrawFee(currency) {
      fetchMultiMchWithdrawFee({ currency }).then(res => {
        if (currency !== this.selectedCurrency) {
          return
        }
        const data = res.data || {}
        this.withdrawFee = data.withdraw_fee || 0
        this.usdWithdrawFee = data.usd_withdraw_fee || 0
        this.withdrawFeeStatus = data.withdraw_fee_status || 0
        this.quotationType = Number(data.quotation_type) || null
        if (this.quotationType === 1) {
          this.rateRequestId += 1
          this.rateLoading = false
          this.exchangeRate = 1
          this.quoteId = null
          this.stopRateRefreshTimer()
        } else if (this.showExchangeRate) {
          this.getExchangeRate(true)
        }
        this.$nextTick(() => {
          if (this.$refs.withdrawForm) {
            this.$refs.withdrawForm.clearValidate('invoice_files')
          }
        })
      })
    },
    getWithdrawThreshold(currency) {
      fetchMultiMchWithdrawType({ currency }).then(res => {
        this.withdrawThreshold = Number((res.data || {}).withdraw_threshold) || 0
      })
    },
    handleAccountChange() {
      this.rateRequestId += 1
      this.rateLoading = false
      this.stopRateRefreshTimer()
      this.exchangeRate = 1
      this.quoteId = null
      this.$nextTick(() => {
        if (this.$refs.withdrawForm) {
          this.$refs.withdrawForm.clearValidate('invoice_files')
        }
      })
      if (!this.showExchangeRate) {
        return
      }
      this.getExchangeRate(true)
    },
    getExchangeRate(force = false) {
      if (this.quotationType === 1 || !this.showExchangeRate || (!force && (this.rateLoading || this.rateRefreshSeconds > 0))) {
        return Promise.resolve()
      }
      const baseCurrency = this.arrivalCurrency
      const quoteCurrency = this.selectedCurrency
      const requestId = ++this.rateRequestId
      this.exchangeRate = 0
      this.quoteId = null
      this.rateLoading = true
      return fetchWithdrawExchangeRate({
        base_currency: baseCurrency,
        quote_currency: quoteCurrency
      }).then(res => {
        if (requestId !== this.rateRequestId || baseCurrency !== this.arrivalCurrency || quoteCurrency !== this.selectedCurrency) {
          return
        }
        const data = res.data || {}
        const exchangeRate = Number(data.mch_rate) || 0
        this.exchangeRate = exchangeRate
        this.quoteId = data.quote_id || null
        if (exchangeRate) {
          this.startRateRefreshTimer(data.expire_time)
        }
      }).finally(() => {
        if (requestId === this.rateRequestId) {
          this.rateLoading = false
        }
      })
    },
    startRateRefreshTimer(expireTime) {
      this.stopRateRefreshTimer()
      const expireTimestamp = new Date(expireTime).getTime()
      if (!expireTime || Number.isNaN(expireTimestamp)) {
        return
      }
      const updateCountdown = () => {
        const remainingSeconds = Math.max(Math.ceil((expireTimestamp - Date.now()) / 1000), 0)
        this.rateRefreshSeconds = remainingSeconds
        if (remainingSeconds === 0) {
          this.rateRefreshTimer = null
          this.getExchangeRate(true)
          return
        }
        this.rateRefreshTimer = window.setTimeout(updateCountdown, 1000)
      }
      this.rateRefreshTimer = window.setTimeout(updateCountdown, 0)
    },
    stopRateRefreshTimer() {
      if (this.rateRefreshTimer) {
        window.clearTimeout(this.rateRefreshTimer)
        this.rateRefreshTimer = null
      }
      this.rateRefreshSeconds = 0
    },
    handleAmountInput() {
      if (this.$refs.withdrawForm) {
        this.$refs.withdrawForm.clearValidate('withdraw_money')
      }
    },
    templateFileSuccess(data = {}) {
      this.withdrawForm.invoice_files.push({
        name: data.fileName,
        invoice_file_name: data.fileName,
        invoice_file_url: data.relativePath
      })
      if (this.$refs.withdrawForm) {
        this.$refs.withdrawForm.clearValidate('invoice_files')
      }
    },
    removeInvoiceFile(index) {
      this.withdrawForm.invoice_files.splice(index, 1)
      if (this.$refs.withdrawForm) {
        this.$refs.withdrawForm.validateField('invoice_files')
      }
    },
    getAccountLabel(item) {
      return [
        item.settlement_bank_account_name,
        item.settlement_type === 1 ? this.selectedCurrency : 'USD',
        item.settlement_bank_account_num
      ].filter(Boolean).join('-')
    },
    formatMinorAmount(value) {
      const formatter = this.$options.filters.toDivideWithDot
      return formatter ? formatter(value) : this.$commonFn.mathDivide(Number(value) || 0, 100).toFixed(2)
    },
    formatMajorAmount(value) {
      const number = Number(value) || 0
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    onClickNext() {
      this.$refs.withdrawForm.validate(valid => {
        if (!valid) {
          return
        }
        const arrivalAmount = Number(String(this.arrivalAmount).replace(/,/g, ''))
        if (this.quotationType !== 1 && !arrivalAmount) {
          this.$message.warning(this.$t('tips.invalid_withdrawal_amount_arrival'))
          return
        }
        const amount = this.$commonFn.mathMultiply(Number(this.withdrawForm.withdraw_money), 100)
        this.stopRateRefreshTimer()
        this.openingConfirm = true
        this.dialogVisible = false
        this.$nextTick(() => {
          this.$refs.confirmModal.changeModalStatus(true, {
            ...this.withdrawForm,
            amount,
            invoice_files: this.withdrawForm.invoice_files.map(file => ({
              invoice_file_name: file.invoice_file_name,
              invoice_file_url: file.invoice_file_url
            })),
            formatted_amount: this.formatMajorAmount(this.withdrawForm.withdraw_money),
            account_label: this.getAccountLabel(this.selectedAccount),
            formatted_fee: this.formattedWithdrawFee,
            arrival_currency: this.arrivalCurrency,
            arrival_amount: this.arrivalAmount,
            exchange_rate: this.exchangeRate,
            quote_id: this.quoteId,
            quotation_type: this.quotationType
          })
        })
      })
    },
    handleWithdrawSuccess(data) {
      this.$emit('get-merchant-info')
      this.withdrawForm = this.createEmptyForm()
      setTimeout(() => {
        this.$refs.withdrawSuccessModal.changModalStatus(true, data.amount, data.currency)
      }, 500)
    },
    handleDialogClosed() {
      if (this.openingConfirm) {
        this.openingConfirm = false
        return
      }
      this.resetWithdrawState()
    },
    resetWithdrawState() {
      this.withdrawForm = this.createEmptyForm()
      this.accountList = []
      this.withdrawFee = 0
      this.usdWithdrawFee = 0
      this.withdrawFeeStatus = 0
      this.withdrawThreshold = 0
      this.quotationType = null
      this.quoteId = null
      this.exchangeRate = 1
      this.rateRequestId += 1
      this.rateLoading = false
      this.stopRateRefreshTimer()
      this.$nextTick(() => {
        if (this.$refs.withdrawForm) {
          this.$refs.withdrawForm.clearValidate()
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.threshold-tip {
  font-size: 10px;
}

.country-tip {
  margin-top: -12px;
}

.dialog-footer {
  margin-top: -16px;
  text-align: center;
}

.btn-submit {
  width: 500px;
  height: 40px;
  margin-top: 36px;
  border-radius: 4px;
}

.icon-question {
  position: relative;
  top: -2px;
  margin-left: 4px;
  font-size: 11px;
  cursor: pointer;
}

.amount-input {
  ::v-deep .el-input-group__prepend {
    width: 90px;
    background: #f5f7fa;
  }

  ::v-deep .el-input-group__prepend .el-select {
    width: 90px;
  }

  // ::v-deep .el-input-group__prepend .el-input__inner {
  //   padding: 0 24px 0 12px;
  //   color: #3855f1;
  //   border: none;
  // }
}

.account-select {
  width: 100%;
}

.readonly-amount {
  display: flex;
  align-items: center;
  height: 38px;
  overflow: hidden;
  color: #4b5567;
  background: #f5f7fa;
  border: 1px solid #98a1b4;
  border-radius: 4px;

  span:first-child {
    display: flex;
    align-items: center;
    align-self: stretch;
    padding: 0 20px;
    color: #3855f1;
    border-right: 1px solid #98a1b4;
  }

  span:last-child {
    padding: 0 15px;
  }
}

.exchange-rate-form-item {
  ::v-deep .el-form-item__label {
    height: 22px;
  }
}

.exchange-rate-wrapper {
  display: flex;
  align-items: center;
  min-height: 38px;
  white-space: nowrap;
  padding-top: 32px;
}

.exchange-rate-text {
  margin-right: 10px;
  color: #303133;
  font-size: 12px;
}

.refresh-rate-button {
  flex-shrink: 0;
  padding: 7px 10px;
  font-size: 11px;
}

.invoice-file-list {
  width: 450px;
  margin-bottom: 10px;
}

.invoice-file-item {
  display: flex;
  align-items: center;
  padding: 7px 10px;
  color: #4b5567;
  background: #f5f7fa;
  border-radius: 4px;

  & + & {
    margin-top: 6px;
  }

  span {
    flex: 1;
    margin: 0 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.invoice-file-remove {
  cursor: pointer;
}

.account-empty-tip {
  margin-top: 8px;
  color: #71110e;
  font-size: 12px;
}

.add-account-link {
  padding-left: 10px;
  color: #3855f1;
  cursor: pointer;
}
</style>
